<template>
  <JBottomSheet
    v-if="isMobile"
    v-model="internalModelValue"
    contentClass="self-start"
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="!isLoading" #header>
      <JContainer class="border-b border-line pb-16 pt-12">
        <h3 class="text-center text-subject font-bold text-dark">
          {{ title }}
        </h3>
      </JContainer>
    </template>

    <TripSwapContent @close="onUpdateModelValue" />
  </JBottomSheet>

  <JModal
    v-else
    v-model="internalModelValue"
    :size="modalSize"
    :title="step !== SwapStep.Complete ? title : undefined"
    noBody
    @update:model-value="onUpdateModelValue"
  >
    <template v-if="step === SwapStep.Complete || isLoading" #header />

    <template v-if="step !== SwapStep.Complete" #navigation>
      <button type="button" @click="helpStore.toggleHelpModal(true)">
        <JIcon
          name="question-mark-outline"
          class="text-metadata text-gray-500"
        />
      </button>
    </template>

    <TripSwapContent @close="onUpdateModelValue" />
  </JModal>
</template>

<script setup lang="ts">
import type { JModalSize } from '@journey/ui-kit'
import { JBottomSheet, JContainer, JIcon, JModal } from '@journey/ui-kit'
import { useVModel } from '@vueuse/core'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import useSwapStore from '../../store/swap'
import { SwapStep } from '../../types'
import type { Emits, Props } from './TripSwap.types'
import TripSwapContent from './TripSwapContent/TripSwapContent.vue'
import useHelpStore from '@/modules/Help/store'
import { useDevicePlatform } from '@/modules/Core/composables'

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { isMobile } = useDevicePlatform()

const swapStore = useSwapStore()
const { getUppercaseSwapType, step, isLoading } = storeToRefs(swapStore)

const internalModelValue = useVModel(props, 'modelValue', emit)

const { t } = useI18n()

const helpStore = useHelpStore()

const title = computed(() =>
  getUppercaseSwapType.value
    ? `${t('words.swap')} ${getUppercaseSwapType.value}`
    : undefined
)

const router = useRouter()
const route = useRoute()

async function onUpdateModelValue() {
  await router.replace({
    name: route.name as string,
  })
}

const modalSize = computed<JModalSize>(() => {
  switch (step.value) {
    case SwapStep.Select:
      return 'lg'
    case SwapStep.Review:
    case SwapStep.Complete:
      return 'md'
    default:
      return 'lg'
  }
})
</script>
